import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { ImageNotSupportedOutlined } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { v2Tokens } from 'theme/v2Theme';

const technicalTextSx = {
  fontFamily:
    '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: { xs: 8, sm: 9 },
  fontWeight: 700,
  letterSpacing: '0.1em',
  lineHeight: 1.4,
  textTransform: 'uppercase',
};

export const gameplayViewportGeometrySx = Object.freeze({
  flex: '0 0 auto',
  aspectRatio: '1 / 1',
  width: '100%',
  height: 'auto',
});

const LiveSimulationPreview = ({ game }) => {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)', {
    noSsr: true,
  });
  const [mediaFailed, setMediaFailed] = useState(false);
  const previewSource = prefersReducedMotion ? game.artwork : game.preview;

  useEffect(() => {
    setMediaFailed(false);
  }, [previewSource]);

  return (
    <Box
      component="section"
      aria-label={`${game.title} gameplay preview`}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'start',
        minWidth: 0,
        width: '100%',
        p: 1.5,
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: v2Tokens.radius.nested,
        backgroundColor: 'rgba(0,0,0,0.52)',
        boxShadow: 'inset 0 0 36px rgba(0,0,0,0.42)',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ minHeight: 22, mb: 1 }}
      >
        <Typography sx={{ ...technicalTextSx, color: '#F7931A' }}>
          Live Gameplay Preview
        </Typography>
        <Typography
          noWrap
          title={`Game: ${game.title}`}
          sx={{ ...technicalTextSx, minWidth: 0, color: '#A3A3A3' }}
        >
          Game: {game.title}
        </Typography>
      </Stack>

      <Box
        sx={{
          position: 'relative',
          display: 'grid',
          placeItems: 'center',
          ...gameplayViewportGeometrySx,
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: v2Tokens.radius.control,
          backgroundColor: v2Tokens.surfaceInset,
          boxShadow:
            'inset 0 0 0 1px rgba(0,0,0,0.28), inset 0 0 32px rgba(0,0,0,0.42)',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            pointerEvents: 'none',
            opacity: 0.34,
            background:
              'repeating-linear-gradient(0deg, rgba(255,255,255,0.035) 0, rgba(255,255,255,0.035) 1px, transparent 1px, transparent 4px)',
          },
        }}
      >
        {mediaFailed ? (
          <Stack
            role="status"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{ p: 3, color: 'text.secondary', textAlign: 'center' }}
          >
            <ImageNotSupportedOutlined aria-hidden="true" />
            <Typography sx={{ ...technicalTextSx, color: '#A3A3A3' }}>
              Gameplay preview unavailable
            </Typography>
          </Stack>
        ) : (
          <Box
            key={previewSource}
            component="img"
            src={previewSource}
            alt={`${game.title} gameplay capture`}
            onError={() => setMediaFailed(true)}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              display: 'block',
              objectFit: 'contain',
              backgroundColor: '#07090D',
            }}
          />
        )}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ minHeight: 22, mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}
      >
        <Typography sx={{ ...technicalTextSx, color: '#737373' }}>
          {prefersReducedMotion
            ? 'Static frame · Reduced motion'
            : 'Looping gameplay capture'}
        </Typography>
        <Typography sx={{ ...technicalTextSx, color: '#E5E5E5' }}>
          Preview only
        </Typography>
      </Stack>
    </Box>
  );
};

LiveSimulationPreview.propTypes = {
  game: PropTypes.shape({
    artwork: PropTypes.string.isRequired,
    preview: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
};

export default LiveSimulationPreview;
